import type { ExportProgress } from '../../utils/downloadExport';

const toMB = (bytes: number): string => {
  const mb = bytes / (1024 * 1024);
  return mb < 10 ? mb.toFixed(1) : String(Math.round(mb));
};

type ExportProgressBarProps = {
  /** null while the server is preparing the list of photos. */
  progress: ExportProgress | null;
};

export default function ExportProgressBar({ progress }: ExportProgressBarProps) {
  if (!progress) {
    return (
      <div className='status-message status-loading'>
        <div className='spinner' aria-hidden='true' />
        <span>Preparing your download&hellip;</span>
      </div>
    );
  }

  const { filesDone, filesTotal, received, total } = progress;
  const complete = filesDone === filesTotal;

  // Exact by bytes when every size is known, otherwise by photo count.
  const fraction = total ? received / total : filesDone / filesTotal;
  const pct = complete ? 100 : Math.min(99, Math.floor(fraction * 100));

  const photos = filesTotal === 1 ? 'photo' : `photo ${Math.min(filesDone + 1, filesTotal)} of ${filesTotal}`;
  const label = complete
    ? 'Saving…'
    : total
      ? `Downloading ${photos} · ${toMB(received)} of ${toMB(total)} MB`
      : `Downloading ${photos} · ${toMB(received)} MB`;

  return (
    <div className='export-progress'>
      <div
        className='upload-progress'
        role='progressbar'
        aria-label='Export download progress'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div className='upload-progress-bar' style={{ width: `${pct}%` }} />
      </div>
      <p className='export-progress-text'>{pct}% · {label}</p>
    </div>
  );
}
